import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Icon from './Icon';
import TextButton from './TextButton';
import { getKeyHandler } from '../structure/FlowForm';
import './MarkdownEditor.css';
import { listIcon, linkIcon } from '../icons';
// Expand the editor's optional extended syntax (enabled via `extendedSyntax`)
// into standard Markdown:
//   [text|url]   -> [text](url)
//   [domain]     -> [domain](https://domain)   (only when it looks like a URL)
//   -strike-     -> ~~strike~~                  (GFM strikethrough)
// Keep this in sync with the server-side copy (blog/server/lib/markdown.ts).
export function expandExtendedMarkdown(text) {
    return text
        .replace(/\[([^\]|\n]+)\|([^\]\n]+)\]/g, (_m, t, u) => `[${t.trim()}](${u.trim()})`)
        .replace(/\[([^\]\s|]+)\](?!\()/g, (m, inner) => {
        const isUrl = /^https?:\/\/\S+$/i.test(inner) || /^[\w-]+(\.[\w-]+)+(\/\S*)?$/.test(inner);
        if (!isUrl)
            return m;
        const url = /^https?:\/\//i.test(inner) ? inner : `https://${inner}`;
        return `[${inner}](${url})`;
    })
        .replace(/(^|[\s(])-(?=\S)([^-\n]+?)(?<=\S)-(?=[\s).,!?;:]|$)/g, (_m, pre, content) => `${pre}~~${content}~~`);
}
// Fine print (image credits, CC-BY attribution, legal notes) is written as
// ^^…^^: Markdown has no syntax of its own for it, and raw HTML is not an option
// because the rendered post escapes every tag (blog/server/lib/markdown.ts).
// Markdown inside the marks still works, so links/emphasis render as usual.
//
// <small>…</small> is the syntax this replaced and is still accepted so that
// posts written with it keep rendering; drop SMALL_OPEN/SMALL_CLOSE (and the
// server's counterpart) once those posts have been re-saved.
const SMALL_MARK = '^^';
const SMALL_OPEN = /^<small\s*>$/i;
const SMALL_CLOSE = /^<\/small\s*>$/i;
// Neither ^^ nor raw HTML means anything to react-markdown: the marks arrive as
// plain text and the legacy tags as html nodes it drops. Both are reduced to the
// same marker node, and each pair of markers then wraps the nodes between them
// in an emphasis node, whose `hName` renders <small> instead of <em>. Working on
// the parsed tree (rather than rewriting the source) keeps the Markdown between
// the marks intact — it is already parsed into the sibling nodes being wrapped.
const MARKER = { type: 'smallMarker' };
function remarkSmall() {
    const isTag = (n, re) => n.type == 'html' && re.test(n.value.trim());
    // Text around/between the ^^ marks becomes text nodes again; the legacy tags
    // are markers on their own already.
    const toMarkers = (children) => children.flatMap((n) => {
        if (isTag(n, SMALL_OPEN) || isTag(n, SMALL_CLOSE))
            return [MARKER];
        // Any other raw HTML is text, as it is in the rendered post. Without this
        // react-markdown would drop it and the preview would hide what the author
        // wrote (e.g. an article about the <title> element).
        if (n.type == 'html')
            return [{ type: 'text', value: n.value }];
        if (n.type != 'text' || !n.value.includes(SMALL_MARK))
            return [n];
        return n.value.split(SMALL_MARK).flatMap((part, i) => [...(i ? [MARKER] : []), ...(part ? [{ type: 'text', value: part }] : [])]);
    });
    const wrap = (node) => {
        if (!node.children)
            return;
        const children = node.children = toMarkers(node.children);
        for (let i = 0; i < children.length; i++) {
            if (children[i] == MARKER) {
                const end = children.findIndex((n, j) => j > i && n == MARKER);
                // An unpaired marker is not fine print, it is what the author typed.
                if (end > i)
                    children.splice(i, end - i + 1, { type: 'emphasis', data: { hName: 'small' }, children: children.slice(i + 1, end) });
                else
                    children[i] = { type: 'text', value: SMALL_MARK };
            }
            wrap(children[i]);
        }
    };
    return (tree) => wrap(tree);
}
// Inline numbered-list icon matching the stroke style of listIcon. Passed to <Icon>
// as a data URI so it is rendered inline rather than fetched as a file.
const listNumberedOutline = 'data:image/svg+xml;utf8,' +
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">' +
    '<line x1="208" y1="128" x2="464" y2="128" stroke="currentColor" stroke-width="36" stroke-linecap="round"/>' +
    '<line x1="208" y1="256" x2="464" y2="256" stroke="currentColor" stroke-width="36" stroke-linecap="round"/>' +
    '<line x1="208" y1="384" x2="464" y2="384" stroke="currentColor" stroke-width="36" stroke-linecap="round"/>' +
    '<text x="88" y="168" font-family="sans-serif" font-size="132" font-weight="600" text-anchor="middle" fill="currentColor">1</text>' +
    '<text x="88" y="296" font-family="sans-serif" font-size="132" font-weight="600" text-anchor="middle" fill="currentColor">2</text>' +
    '<text x="88" y="424" font-family="sans-serif" font-size="132" font-weight="600" text-anchor="middle" fill="currentColor">3</text>' +
    '</svg>';
const MarkdownEditor = ({ value, onChange, disabled = false, placeholder = '', showToolbar = true, maxLength, compact = false, className = '', transparentToolbar = false, extendedSyntax = false, headlineLevel = 2 }) => {
    const textareaRef = useRef(null);
    const [showPreview, setShowPreview] = useState(false);
    // Check if content has 3+ newlines to auto-expand
    const newlineCount = (value.match(/\n/g) || []).length;
    const shouldExpand = newlineCount >= 3;
    const isCompact = compact && !shouldExpand;
    const insertAtCursor = (before, after = '') => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        const selectedText = text.substring(start, end);
        const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
        onChange(newText);
        // Restore focus and set cursor position
        setTimeout(() => {
            textarea.focus();
            const newCursorPos = start + before.length + selectedText.length;
            textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };
    const autoLinkUrls = (text) => {
        // URL pattern that matches http://, https://, and www. URLs
        const urlPattern = /(?:^|\s)((?:https?:\/\/|www\.)[^\s]+)/gi;
        return text.replace(urlPattern, (match, url, offset) => {
            // Check if already in markdown format [text](url)
            const beforeMatch = text.substring(Math.max(0, offset - 2), offset);
            const afterMatch = text.substring(offset + match.length, offset + match.length + 1);
            // Skip if already part of markdown link
            if (beforeMatch.endsWith('](') || afterMatch.startsWith(')')) {
                return match;
            }
            // Create link text without http:// or https://
            const linkText = url.replace(/^https?:\/\//, '');
            const whitespace = match.substring(0, match.length - url.length);
            return `${whitespace}[${linkText}](${url})`;
        });
    };
    const toggleHeadline = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const text = textarea.value;
        // Find the start of the current line
        let lineStart = start;
        while (lineStart > 0 && text[lineStart - 1] != '\n') {
            lineStart--;
        }
        // Remove an existing heading prefix of any level, otherwise add ours
        const lineText = text.substring(lineStart);
        const existing = /^#{1,6} ?/.exec(lineText);
        const prefix = '#'.repeat(headlineLevel) + ' ';
        const [newText, shift] = existing
            ? [text.substring(0, lineStart) + lineText.substring(existing[0].length), -existing[0].length]
            : [text.substring(0, lineStart) + prefix + lineText, prefix.length];
        onChange(newText);
        setTimeout(() => {
            textarea.focus();
            const pos = Math.max(lineStart, start + shift);
            textarea.setSelectionRange(pos, pos);
        }, 0);
    };
    const toggleBold = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = textarea.value;
        // Find if cursor/selection is within ** markers on the current line
        // Search backwards for opening **
        let openPos = -1;
        for (let i = start - 1; i >= 0; i--) {
            if (text[i] == '\n')
                break; // Stop at line boundary
            if (i >= 1 && text.substring(i - 1, i + 1) == '**') {
                openPos = i - 1;
                break;
            }
        }
        // Search forwards for closing **
        let closePos = -1;
        if (openPos >= 0) {
            for (let i = end; i < text.length - 1; i++) {
                if (text[i] == '\n')
                    break; // Stop at line boundary
                if (text.substring(i, i + 2) == '**') {
                    closePos = i;
                    break;
                }
            }
        }
        // If we found both markers, remove them
        if (openPos >= 0 && closePos >= 0) {
            const beforeBold = text.substring(0, openPos);
            const boldText = text.substring(openPos + 2, closePos);
            const afterBold = text.substring(closePos + 2);
            const newText = beforeBold + boldText + afterBold;
            onChange(newText);
            // Adjust cursor position
            let newStart = start;
            let newEnd = end;
            if (start > openPos + 2)
                newStart -= 2;
            if (end > openPos + 2)
                newEnd -= 2;
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(newStart, newEnd);
            }, 0);
        }
        else {
            // Add **
            insertAtCursor('**', '**');
        }
    };
    const addBulletList = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const text = textarea.value;
        // Find the start of the current line
        let lineStart = start;
        while (lineStart > 0 && text[lineStart - 1] != '\n') {
            lineStart--;
        }
        const lineText = text.substring(lineStart);
        if (lineText.startsWith('- ')) {
            // Remove the bullet list marker
            const newText = text.substring(0, lineStart) + lineText.substring(2);
            onChange(newText);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start - 2, start - 2);
            }, 0);
        }
        else {
            // Add bullet list marker
            const newText = text.substring(0, lineStart) + '- ' + lineText;
            onChange(newText);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + 2, start + 2);
            }, 0);
        }
    };
    const addNumberedList = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const start = textarea.selectionStart;
        const text = textarea.value;
        // Find the start of the current line
        let lineStart = start;
        while (lineStart > 0 && text[lineStart - 1] != '\n') {
            lineStart--;
        }
        const lineText = text.substring(lineStart);
        const numberedMatch = lineText.match(/^(\d+)\. /);
        if (numberedMatch) {
            // Remove the numbered list marker
            const markerLength = numberedMatch[0].length;
            const newText = text.substring(0, lineStart) + lineText.substring(markerLength);
            onChange(newText);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start - markerLength, start - markerLength);
                // Renumber remaining lists
                renumberLists();
            }, 0);
        }
        else {
            // Add numbered list marker
            const newText = text.substring(0, lineStart) + '1. ' + lineText;
            onChange(newText);
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(start + 3, start + 3);
            }, 0);
        }
    };
    const toggleStrike = () => insertAtCursor('-', '-');
    const toggleSmall = () => insertAtCursor(SMALL_MARK, SMALL_MARK);
    const addLink = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const hasSelection = textarea.selectionStart != textarea.selectionEnd;
        if (hasSelection)
            insertAtCursor('[', '|https://]'); // wrap selection: [selection|https://]
        else
            insertAtCursor('[text|https://]'); // insert a template to fill in
    };
    const handleKeyDown = (e) => {
        var _a, _b;
        const textarea = e.currentTarget;
        // Suppress Home/End page navigation when textarea is focused
        if (e.key == 'Home' || e.key == 'End') {
            e.stopPropagation();
            e.preventDefault();
            // Manually handle cursor positioning within textarea
            if (e.key == 'Home') {
                // Move to start of current line
                const text = textarea.value;
                const pos = textarea.selectionStart;
                let lineStart = pos;
                while (lineStart > 0 && text[lineStart - 1] != '\n') {
                    lineStart--;
                }
                textarea.setSelectionRange(lineStart, lineStart);
            }
            else {
                // Move to end of current line
                const text = textarea.value;
                const pos = textarea.selectionStart;
                let lineEnd = pos;
                while (lineEnd < text.length && text[lineEnd] != '\n') {
                    lineEnd++;
                }
                textarea.setSelectionRange(lineEnd, lineEnd);
            }
            return;
        }
        // Handle Page Up/Down
        if (e.key == 'PageUp' || e.key == 'PageDown') {
            const atBeginning = textarea.selectionStart == 0;
            const atEnd = textarea.selectionStart == textarea.value.length;
            if (e.key == 'PageUp') {
                if (!atBeginning) {
                    // Not at beginning, move cursor to start
                    e.stopPropagation();
                    e.preventDefault();
                    textarea.setSelectionRange(0, 0);
                    return;
                }
                // At beginning, allow page navigation
            }
            else {
                if (!atEnd) {
                    // Not at end, move cursor to end
                    e.stopPropagation();
                    e.preventDefault();
                    const endPos = textarea.value.length;
                    textarea.setSelectionRange(endPos, endPos);
                    return;
                }
                // At end, allow page navigation
            }
        }
        if (e.key == 'Enter' && !e.shiftKey) {
            const textarea = textareaRef.current;
            if (!textarea)
                return;
            const start = textarea.selectionStart;
            const text = textarea.value;
            // Find the start of the current line
            let lineStart = start;
            while (lineStart > 0 && text[lineStart - 1] != '\n') {
                lineStart--;
            }
            const lineText = text.substring(lineStart, start);
            // Check if it's a bullet list
            if (lineText.trimStart().startsWith('- ')) {
                e.preventDefault();
                const indent = ((_a = lineText.match(/^(\s*)/)) === null || _a === void 0 ? void 0 : _a[1]) || '';
                insertAtCursor('\n' + indent + '- ');
                return;
            }
            // Check if it's a numbered list
            const numberedMatch = lineText.trimStart().match(/^(\d+)\.\s/);
            if (numberedMatch) {
                e.preventDefault();
                const indent = ((_b = lineText.match(/^(\s*)/)) === null || _b === void 0 ? void 0 : _b[1]) || '';
                const nextNum = parseInt(numberedMatch[1]) + 1;
                insertAtCursor('\n' + indent + nextNum + '. ');
                // Renumber lists after insertion
                setTimeout(() => renumberLists(), 0);
                return;
            }
        }
        // Handle backspace to renumber lists
        if (e.key == 'Backspace') {
            const textarea = textareaRef.current;
            if (!textarea)
                return;
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            // Only handle if deleting a line (selection spans newline or at start of line)
            if (start != end || start == 0)
                return;
            const text = textarea.value;
            if (text[start - 1] == '\n') {
                // Deleting a line break - check if we need to renumber
                setTimeout(() => renumberLists(), 0);
            }
        }
        // Default flow form key handling
        const flowHandler = getKeyHandler({ multiLine: true });
        flowHandler(e);
    };
    const renumberLists = () => {
        const textarea = textareaRef.current;
        if (!textarea)
            return;
        const lines = textarea.value.split('\n');
        let changed = false;
        const newLines = [];
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const match = line.match(/^(\s*)(\d+)(\.\s.*)$/);
            if (match) {
                const indent = match[1];
                const content = match[3];
                // Find expected number by looking at previous numbered line with same indent
                let expectedNum = 1;
                for (let j = i - 1; j >= 0; j--) {
                    const prevMatch = newLines[j].match(/^(\s*)(\d+)(\.\s.*)$/);
                    if (prevMatch && prevMatch[1] == indent) {
                        expectedNum = parseInt(prevMatch[2]) + 1;
                        break;
                    }
                    // Stop if we hit a non-list line or different indent
                    if (!newLines[j].trim().startsWith('-') && !prevMatch) {
                        break;
                    }
                }
                if (parseInt(match[2]) != expectedNum) {
                    newLines.push(indent + expectedNum + content);
                    changed = true;
                }
                else {
                    newLines.push(line);
                }
            }
            else {
                newLines.push(line);
            }
        }
        if (changed) {
            const cursorPos = textarea.selectionStart;
            onChange(newLines.join('\n'));
            setTimeout(() => {
                textarea.focus();
                textarea.setSelectionRange(cursorPos, cursorPos);
            }, 0);
        }
    };
    return (_jsxs("div", { className: `markdownEditor ${isCompact ? 'compact' : ''} ${className}`, children: [showToolbar && (_jsxs("div", { className: `markdownToolbar ${transparentToolbar ? 'transparent' : ''}`, children: [!showPreview && (_jsxs(_Fragment, { children: [_jsx("button", { className: "markdownToolbarButton flowFocussable", onClick: toggleHeadline, onKeyDown: getKeyHandler(toggleHeadline), disabled: disabled, title: "Headline", tabIndex: 0, children: _jsx("strong", { children: "H" }) }), _jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: toggleBold, onKeyDown: getKeyHandler(toggleBold), disabled: disabled, title: "Bold", tabIndex: 0, children: _jsx("strong", { children: "B" }) }), _jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: addBulletList, onKeyDown: getKeyHandler(addBulletList), disabled: disabled, title: "Bullet List", tabIndex: 0, children: _jsx(Icon, { icon: listIcon, size: "large" }) }), _jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: addNumberedList, onKeyDown: getKeyHandler(addNumberedList), disabled: disabled, title: "Numbered List", tabIndex: 0, children: _jsx(Icon, { icon: listNumberedOutline, size: "large" }) }), extendedSyntax && _jsxs(_Fragment, { children: [_jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: addLink, onKeyDown: getKeyHandler(addLink), disabled: disabled, title: "Link ([text|url])", tabIndex: 0, children: _jsx(Icon, { icon: linkIcon, size: "large" }) }), _jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: toggleStrike, onKeyDown: getKeyHandler(toggleStrike), disabled: disabled, title: "Strikethrough (-text-)", tabIndex: 0, children: _jsx("span", { style: { textDecoration: 'line-through' }, children: "S" }) }), _jsx("button", { className: "markdownToolbarButton flowFocussable sidewayFocus", onClick: toggleSmall, onKeyDown: getKeyHandler(toggleSmall), disabled: disabled, title: "Fine print (^^text^^)", tabIndex: 0, children: _jsx("span", { style: { fontSize: '0.72em' }, children: "Aa" }) })] }), _jsx("div", { className: "markdownToolbarSpacer" })] })), _jsx(TextButton, { className: "previewToggleButton", label: showPreview ? 'Edit' : 'Preview', onClick: () => setShowPreview(!showPreview), enabled: disabled ? 'disabled' : 'enabled', size: 'small', type: 'minor' })] })), showPreview ? (_jsx("div", { className: "markdownPreview", children: _jsx(ReactMarkdown, { remarkPlugins: extendedSyntax ? [remarkGfm, remarkSmall] : [], children: extendedSyntax ? expandExtendedMarkdown(autoLinkUrls(value)) : autoLinkUrls(value) }) })) : (_jsx("textarea", { ref: textareaRef, className: "markdownTextarea flowFocussable", value: value, onChange: (e) => onChange(e.target.value), onKeyDown: handleKeyDown, disabled: disabled, placeholder: placeholder, tabIndex: 0, rows: isCompact ? 4 : 10, maxLength: maxLength }))] }));
};
export default MarkdownEditor;
